import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Brain, Users, Building2, TrendingUp } from "lucide-react";

const STEPS = [
  { id: "01", icon: Sparkles, title: "Creators publish", desc: "High-integrity content from vetted creators enters the F3 graph." },
  { id: "02", icon: Brain, title: "AI organizes signal", desc: "F3 Discover indexes, weights and ranks by depth — not engagement bait." },
  { id: "03", icon: Users, title: "Viewers engage deeply", desc: "Directed-intent UX rewards focus, recall and informed action." },
  { id: "04", icon: Building2, title: "Brands reach intent", desc: "Premium adjacency to high-value, high-intent audiences with real lift." },
  { id: "05", icon: TrendingUp, title: "Value compounds", desc: "Every signal feeds back: better creators, deeper viewers, higher yield." },
];

const FLY_NODES = ["Create", "Curate", "Reward", "Scale"];

export default function Solutions() {
  return (
    <section
      id="solutions"
      data-testid="section-solutions"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-mono tracking-[0.3em] text-violet-300 uppercase mb-6">
              Chapter 03 · How it works
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-[1.0]">
              The compounding
              <br />
              <span className="text-zinc-400">flywheel</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-zinc-400 text-base leading-relaxed">
              Every loop reinforces the next. Output becomes input. Quality
              compounds.
            </p>
          </div>
        </div>

        {/* Flywheel SVG */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20">
          <div className="lg:col-span-6">
            <div className="relative aspect-square max-w-[570px] mx-auto">
              {/* ambient purple halo — softer */}
              <div className="absolute inset-12 rounded-full bg-violet-700/12 blur-3xl pointer-events-none" />

              <svg viewBox="-30 -30 460 460" className="relative w-full h-full" data-testid="solutions-flywheel">
                <defs>
                  <linearGradient id="arc" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity="1" />
                    <stop offset="50%" stopColor="#7c3aed" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity="0.2" />
                  </linearGradient>
                  <filter id="purple-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* outer ring base */}
                <circle cx="200" cy="200" r="170" fill="none" stroke="rgba(168,85,247,0.18)" strokeWidth="1.5" />

                {/* glowing rotating arc — clockwise (arc only inside glow filter) */}
                <g className="spin-slow" style={{ transformOrigin: "200px 200px" }}>
                  <g style={{ filter: "url(#purple-glow)" }}>
                    <circle
                      cx="200"
                      cy="200"
                      r="170"
                      fill="none"
                      stroke="url(#arc)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray="180 800"
                    />
                  </g>
                  {/* lead dot — sharp, at the head of the moving arc (end of the visible dasharray, leading the motion) */}
                  <circle cx="283" cy="348" r="6" fill="#ffffff" />
                  <circle cx="283" cy="348" r="2.8" fill="#7c3aed" />
                </g>

                {/* inner ring */}
                <circle cx="200" cy="200" r="110" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="2 8" />

                {/* center label */}
                <text x="200" y="194" textAnchor="middle" fill="#c4b5fd" fontSize="10" fontFamily="JetBrains Mono" letterSpacing="3">F3 / FLYWHEEL</text>
                <text x="200" y="222" textAnchor="middle" fill="#ffffff" fontFamily="PT Serif" fontStyle="italic" fontSize="26">Compounding</text>

                {/* 4 quadrant nodes — sharp outline, no halo */}
                {FLY_NODES.map((n, i) => {
                  const angle = (i * 90 - 90) * (Math.PI / 180);
                  const x = 200 + Math.cos(angle) * 170;
                  const y = 200 + Math.sin(angle) * 170;
                  return (
                    <g key={n}>
                      <circle cx={x} cy={y} r="38" fill="#000000" stroke="rgba(168, 85, 247, 0.5)" strokeWidth="1.25" />
                      <text x={x} y={y + 5} textAnchor="middle" fill="#fff" fontSize="13" fontFamily="PT Serif">{n}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {FLY_NODES.map((n, i) => (
                <div key={n} className="bg-black p-6 md:p-8">
                  <div className="text-[10px] font-mono text-zinc-500 mb-2">0{i + 1}</div>
                  <div className="font-heading text-2xl text-white mb-2">{n}</div>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    {[
                      "Creators ship signal-rich content into the F3 graph.",
                      "AI ranks by integrity, depth and contextual fit.",
                      "Viewers and creators are rewarded for genuine engagement.",
                      "The graph scales with each loop — quality compounds.",
                    ][i]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 5-step ecosystem flow */}
        <div className="border-t border-white/10 pt-16">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-10">
            5-step ecosystem flow
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`flow-step-${s.id}`}
                  className="group bg-black hover:bg-zinc-950 transition-colors p-6 md:p-7 relative"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="font-mono text-[11px] tracking-[0.25em] text-violet-300">
                      {s.id}
                    </span>
                    <Icon className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <div className="font-heading text-lg text-white leading-snug mb-2">{s.title}</div>
                  <p className="text-xs text-zinc-500 leading-relaxed">{s.desc}</p>
                  {i < STEPS.length - 1 && (
                    <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 text-violet-300 text-xs font-mono">
                      →
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
